import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FileText, CheckCircle, AlertCircle, Building, Phone, Mail,
  MapPin, Calendar, Euro, Download, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { SignatureCanvas } from '../../components/ui/signature-canvas';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

export default function PublicQuoteSignPage() {
  const { quoteId } = useParams();
  const [quote, setQuote] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signed, setSigned] = useState(false);
  const [signing, setSigning] = useState(false);
  
  const [signatureData, setSignatureData] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [showCGV, setShowCGV] = useState(false);

  useEffect(() => {
    fetchQuote();
  }, [quoteId]);

  const fetchQuote = async () => {
    try {
      const response = await fetch(`${API_URL}/api/batiment/quotes/public/${quoteId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Devis introuvable');
        } else {
          setError('Erreur lors du chargement du devis');
        }
        return;
      }

      const data = await response.json();
      setQuote(data.quote);
      setCompany(data.company);
      
      if (data.quote.signed) {
        setSigned(true);
      }
    } catch (err) {
      setError('Impossible de charger le devis');
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!signatureData) {
      toast.error('Veuillez signer le devis');
      return;
    }
    if (!signerName.trim()) {
      toast.error('Entre ton nom');
      return;
    }
    if (!cgvAccepted) {
      toast.error('Veuillez accepter les conditions générales de vente');
      return;
    }

    setSigning(true);

    try {
      // First upload signature image
      const blob = await fetch(signatureData).then(r => r.blob());
      const formData = new FormData();
      formData.append('file', blob, 'signature.png');

      const uploadResponse = await fetch(`${API_URL}/api/files/upload`, {
        method: 'POST',
        body: formData
      });

      let signatureUrl = signatureData;
      if (uploadResponse.ok) {
        const uploadData = await uploadResponse.json();
        signatureUrl = uploadData.file_url;
      }

      // Then sign the quote
      const response = await fetch(
        `${API_URL}/api/batiment/quotes/public/${quoteId}/sign?signature_url=${encodeURIComponent(signatureUrl)}&signed_by=${encodeURIComponent(signerName)}&cgv_accepted=true`,
        { method: 'POST' }
      );

      if (response.ok) {
        setSigned(true);
        toast.success('Devis signé avec succès !');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur lors de la signature');
      }
    } catch (err) {
      toast.error('Erreur lors de la signature');
    } finally {
      setSigning(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Chargement du devis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">Oops !</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
          >
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
          </motion.div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Devis signé !</h1>
          <p className="text-slate-600 mb-6">
            Merci {quote?.signed_by || signerName}, ta signature a été enregistrée.
          </p>
          <div className="bg-green-50 rounded-xl p-4 text-left">
            <p className="text-sm text-green-700">
              <strong>Devis :</strong> {quote?.quote_number}
            </p>
            <p className="text-sm text-green-700 mt-1">
              <strong>Montant :</strong> {formatCurrency(quote?.total)}
            </p>
            <p className="text-sm text-green-700 mt-1">
              <strong>Signé le :</strong> {formatDate(quote?.signed_at || new Date().toISOString())}
            </p>
          </div>
          <p className="text-xs text-slate-400 mt-6">
            Un email de confirmation te sera envoyé prochainement.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              {company?.logo_url ? (
                <img src={company.logo_url} alt={company.company_name} className="h-12 mb-4" />
              ) : (
                <h1 className="text-2xl font-bold text-slate-800">{company?.company_name || 'Devis'}</h1>
              )}
              {company?.company_name && !company?.logo_url && null}
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-blue-600">{quote?.quote_number}</p>
              <p className="text-sm text-slate-500">Créé le {formatDate(quote?.created_at)}</p>
            </div>
          </div>

          {/* Company & Client Info */}
          <div className="grid md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-slate-100">
            <div>
              <h3 className="text-xs font-semibold text-slate-400 uppercase mb-2">De</h3>
              <p className="font-medium">{company?.company_name}</p>
              {company?.address && <p className="text-sm text-slate-600">{company.address}</p>}
              {company?.city && <p className="text-sm text-slate-600">{company.postal_code} {company.city}</p>}
              {company?.phone && <p className="text-sm text-slate-600">{company.phone}</p>}
              {company?.siret && <p className="text-sm text-slate-400 mt-1">SIRET: {company.siret}</p>}
            </div>
            <div>
              <h3 className="text-xs font-semibold text-slate-400 uppercase mb-2">Pour</h3>
              <p className="font-medium">{quote?.client_name}</p>
              {quote?.client_address && <p className="text-sm text-slate-600">{quote.client_address}</p>}
              {quote?.client_email && (
                <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                  <Mail className="w-3 h-3" />
                  {quote.client_email}
                </p>
              )}
              {quote?.client_phone && (
                <p className="text-sm text-slate-600 flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {quote.client_phone}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Quote Details */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-bold text-slate-800 mb-4">Description des travaux</h2>
          <p className="text-slate-700 whitespace-pre-line">{quote?.work_description}</p>

          {quote?.chantier_address && (
            <div className="mt-4 flex items-center gap-2 text-slate-600">
              <MapPin className="w-4 h-4" />
              <span className="text-sm">Adresse du chantier : {quote.chantier_address}</span>
            </div>
          )}
        </div>

        {/* Items Table */}
        {quote?.items?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-6">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-4 text-sm font-semibold text-slate-600">Description</th>
                  <th className="text-right p-4 text-sm font-semibold text-slate-600">Qté</th>
                  <th className="text-right p-4 text-sm font-semibold text-slate-600">Prix unit.</th>
                  <th className="text-right p-4 text-sm font-semibold text-slate-600">Total</th>
                </tr>
              </thead>
              <tbody>
                {quote.items.map((item, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="p-4">{item.description}</td>
                    <td className="p-4 text-right">{item.quantity} {item.unit}</td>
                    <td className="p-4 text-right">{formatCurrency(item.unit_price)}</td>
                    <td className="p-4 text-right font-medium">{formatCurrency(item.quantity * item.unit_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Totals */}
            <div className="bg-slate-50 p-4">
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Sous-total HT</span>
                    <span>{formatCurrency(quote.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">TVA ({quote.tax_rate}%)</span>
                    <span>{formatCurrency(quote.tax_amount)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t border-slate-200 pt-2">
                    <span>Total TTC</span>
                    <span className="text-blue-600">{formatCurrency(quote.total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Validity */}
        <div className="bg-amber-50 rounded-xl p-4 mb-6 flex items-center gap-3">
          <Calendar className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-700">
            Ce devis est valable jusqu'au <strong>{formatDate(quote?.valid_until)}</strong>
          </p>
        </div>

        {/* Signature Section */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-4">Signature du devis</h2>
          
          {/* Signer Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Ton nom complet *
            </label>
            <input
              type="text"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Jean Dupont"
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </div>

          {/* Signature Canvas */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Ta signature *
            </label>
            <SignatureCanvas 
              onSignatureChange={setSignatureData}
              width={400}
              height={200}
            />
          </div>

          {/* CGV Acceptance */}
          <div className="mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={cgvAccepted}
                onChange={(e) => setCgvAccepted(e.target.checked)}
                className="mt-1 w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-slate-600">
                J'accepte les{' '}
                <button
                  type="button"
                  onClick={() => setShowCGV(true)}
                  className="text-blue-600 hover:underline"
                >
                  conditions générales de vente
                </button>{' '}
                et je m'engage à régler le montant indiqué selon les modalités convenues. *
              </span>
            </label>
          </div>

          {/* Sign Button */}
          <Button
            onClick={handleSign}
            disabled={signing || !signatureData || !signerName || !cgvAccepted}
            className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
          >
            {signing ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Signature en cours...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                Signer et accepter le devis
              </>
            )}
          </Button>

          <p className="text-xs text-slate-400 text-center mt-4">
            En signant ce document, tu acceptes le devis et les conditions générales de vente.
          </p>
        </div>

        {/* Notes */}
        {quote?.notes && (
          <div className="bg-white rounded-2xl shadow-sm p-6 mt-6">
            <h3 className="font-semibold text-slate-800 mb-2">Notes</h3>
            <p className="text-sm text-slate-600">{quote.notes}</p>
          </div>
        )}
      </div>

      {/* CGV Modal */}
      {showCGV && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowCGV(false)}
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-100">
              <h2 className="text-xl font-bold">Conditions Générales de Vente</h2>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {company?.cgv_text ? (
                <div className="prose prose-sm" dangerouslySetInnerHTML={{ __html: company.cgv_text.replace(/\n/g, '<br/>') }} />
              ) : (
                <div className="text-sm text-slate-600 space-y-4">
                  <p><strong>Article 1 - Objet</strong><br/>
                  Les présentes conditions générales de vente régissent les relations contractuelles entre le prestataire et le client.</p>
                  
                  <p><strong>Article 2 - Devis</strong><br/>
                  Les devis sont valables pour la durée indiquée. L'acceptation du devis par signature vaut commande ferme.</p>
                  
                  <p><strong>Article 3 - Prix</strong><br/>
                  Les prix sont indiqués en euros TTC. Ils comprennent la fourniture et la pose des matériaux mentionnés.</p>
                  
                  <p><strong>Article 4 - Paiement</strong><br/>
                  Le paiement s'effectue selon les modalités indiquées sur le devis. Un acompte peut être demandé à la commande.</p>
                  
                  <p><strong>Article 5 - Délais</strong><br/>
                  Les délais d'exécution sont donnés à titre indicatif. Tout retard ne peut donner lieu à des dommages et intérêts.</p>
                  
                  <p><strong>Article 6 - Garantie</strong><br/>
                  Les travaux sont garantis conformément à la législation en vigueur (garantie décennale, biennale).</p>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-slate-100 flex justify-end">
              <Button onClick={() => setShowCGV(false)}>Fermer</Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
